import React, { useState, useRef, useEffect } from 'react';
import { GoogleMap, Marker, useLoadScript, Autocomplete } from '@react-google-maps/api';
import { InputAdornment, IconButton, Box, Typography, Paper } from '@mui/material';

import EditLocationIcon from '@mui/icons-material/EditLocation';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CircleIcon from '@mui/icons-material/Circle';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const libraries = ['places'];

const mapContainerStyle = {
  width: '100%',
  height: '400px',
};

// Default coordinates for Argentina
const defaultCenter = {
  lat: -38.416097,  // Approximate latitude for central Argentina
  lng: -63.616672,  // Approximate longitude for central Argentina
};

const MapWithAutocomplete = ({ onLocationSelect }) => {
  const [map, setMap] = useState(null);
  const [center, setCenter] = useState(defaultCenter);
  const [address, setAddress] = useState('');
  const [detailedLocation, setDetailedLocation] = useState('');
  const [markerPosition, setMarkerPosition] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const autocompleteRef = useRef(null);
  const geocoder = useRef(null);

  const isLoaded = window.google && window.google.maps;

  useEffect(() => {
    // Get user's location if geolocation is available
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setCenter(userLocation);
          setMarkerPosition(userLocation);
        },
        (error) => {
          console.log("Error getting location:", error);
          // Keep default center if geolocation fails
          setCenter(defaultCenter);
          setMarkerPosition(defaultCenter);
        }
      );
    }

    if (isLoaded && !geocoder.current) {
      geocoder.current = new window.google.maps.Geocoder();
    }
  }, [isLoaded]);

  const handlePlaceSelect = () => {
    const place = autocompleteRef.current.getPlace();
    if (place.geometry) {
      const location = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      };
      setCenter(location);
      setMarkerPosition(location);
      setAddress(place.formatted_address);
      setInputValue(place.formatted_address);
      if (onLocationSelect) {
        onLocationSelect({
          address: place.formatted_address,
          coordinates: location
        });
      }
      if (map) {
        map.panTo(location);
      }
    }
  };

  const handleMapIdle = () => {
    if (map && geocoder.current) {
      const center = map.getCenter();
      const location = {
        lat: center.lat(),
        lng: center.lng()
      };
      setMarkerPosition(location);

      geocoder.current.geocode({ location }, (results, status) => {
        if (status === 'OK' && results[0]) {
          // Si el input está vacío, actualizamos la dirección y las coordenadas
          if (!inputValue) {
            setDetailedLocation(results[0].formatted_address);
            if (onLocationSelect) {
              onLocationSelect({
                address: results[0].formatted_address,
                coordinates: location
              });
            }
          } else {
            // Si el input tiene texto, solo actualizamos las coordenadas
            if (onLocationSelect) {
              onLocationSelect({
                address: inputValue,
                coordinates: location
              });
            }
          }
        }
      });
    }
  };

  if (!isLoaded) return <div>Cargando el mapa...</div>;

  return (
    <div style={{ width: '100%', marginBottom: 16 }}>
      <Autocomplete
        onLoad={ref => (autocompleteRef.current = ref)}
        onPlaceChanged={handlePlaceSelect}
      >
        <input
          type="text"
          placeholder="Ubicación precisa"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          style={{
            boxSizing: `border-box`,
            border: `1px solid transparent`,
            width: `100%`,
            height: `40px`,
            padding: `0 12px`,
            borderRadius: `3px`,
            boxShadow: `0 2px 6px rgba(0, 0, 0, 0.3)`,
            fontSize: `14px`,
            textAlign: 'center',
            outline: `none`,
            textOverflow: `ellipses`,
            marginBottom: 8
          }}
        />
      </Autocomplete>

      {/* Mostrar el nombre de la calle o la ubicación ingresada manualmente */}
      {detailedLocation && !inputValue && (
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            height: 20,
            alignItems: 'center',
            justifyContent: 'center',
            padding: '6px 12px',
            marginBottom: 1,
            transition: 'background-color 0.3s ease',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        >
          <LocationOnIcon sx={{ fontSize: 16, marginRight: 0.5, color: 'primary.main' }} />
          <Typography variant="body2" color={'text.primary'} style={{ textAlign: 'center' }}>
            {detailedLocation}
          </Typography>
        </Paper>
      )}

      <div style={{ position: 'relative' }}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={15}
          options={{
            gestureHandling: 'greedy',
            disableDefaultUI: true,
            zoomControl: true
          }}
          onLoad={map => setMap(map)}
          onIdle={handleMapIdle}
        >
          {markerPosition && (
            <Marker
              position={markerPosition}
            />
          )}
        </GoogleMap>

        <CircleIcon
          style={{
            position: 'absolute',
            color: '#f44336',
            top: 'calc(50% - 4px)',
            left: 'calc(50% - 4px)',
            fontSize: 8,
            zIndex: 1,
            pointerEvents: 'none',
            filter: 'drop-shadow(0px 0px 2px rgba(255, 255, 255, 0.8))'
          }}
        />
      </div>
    </div>
  );
};

export default MapWithAutocomplete;